import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';

import moment from 'moment-timezone';

import type { AutomatedActionDueDate } from '@core/automated-actions-types';
import { I18nService } from '@core/i18n-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';

import { DueDatePickerComponent } from '../due-date-picker/due-date-picker.component';

@Component({
  selector: 'rt-due-date-field',
  templateUrl: './due-date-field.component.html',
  styleUrls: ['./due-date-field.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.variant-button]': "variant() === 'button'",
  },
  imports: [
    InputComponent,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    ProtectedControlDirective,
    DueDatePickerComponent,
  ],
})
export class DueDateFieldComponent {
  private readonly i18n = inject(I18nService);

  private readonly unitPluralKey: { [key: string]: string } = {
    days: 'app.plurals.day',
    weeks: 'app.plurals.week',
    months: 'app.plurals.month',
  };

  protected readonly dropdownPositionPairs: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  public readonly value = input<AutomatedActionDueDate | null>(null);
  public readonly variant = input<'input' | 'button'>('input');
  public readonly showTimezone = input<boolean>(true);
  public readonly relativeLabel = input<string>(
    this.i18n.translate('app.automated_actions.action_editor.due_date_in'),
  );
  public readonly submitLabel = input<string>(this.i18n.translate('app.common.done'));
  public readonly placeholder = input<string>('');
  public readonly emptyLabel = input<string>('');
  public readonly triggerIcon = input<IconName | undefined>(undefined);
  public readonly disabled = input<boolean>(false);
  public readonly protectedControl = input<AbstractControl | null | undefined>(undefined);

  public readonly valueChange = output<AutomatedActionDueDate | null>();

  protected overlay = viewChild(ConnectedOverlayDirective);
  protected dropdownOpened = signal(false);

  protected effectiveIcon = computed<IconName>(
    () => this.triggerIcon() ?? (this.variant() === 'button' ? 'access-time' : 'calendar-today'),
  );
  protected display = computed<string | null>(() => {
    const value = this.value();

    if (!value) {
      return null;
    }

    if (value.dueDateType === 'absolute') {
      return value.timezone
        ? moment.tz(value.dueDateValue, value.timezone).format('DD MMM YYYY')
        : moment.utc(value.dueDateValue).format('DD MMM YYYY');
    }

    return `${value.dueDateValue} ${this.translateUnit(value.dueDateUnit, value.dueDateValue)}`;
  });
  protected pickerType = computed(() => this.value()?.dueDateType);
  protected pickerUnit = computed(() => {
    const value = this.value();

    return value && value.dueDateType === 'relative' ? value.dueDateUnit : undefined;
  });
  protected pickerValue = computed(() => this.value()?.dueDateValue?.toString());
  protected pickerTimezone = computed(() => this.value()?.timezone);

  protected toggleDropdown(): void {
    this.dropdownOpened.set(!this.dropdownOpened());
  }

  protected onSelect(dueDate: AutomatedActionDueDate): void {
    this.valueChange.emit(dueDate);
    this.dropdownOpened.set(false);
  }

  protected clear(): void {
    this.valueChange.emit(null);
  }

  protected onTypeChange(): void {
    this.overlay()?.overlayRef.updatePosition();
  }

  private translateUnit(unit: string | null | undefined, count: string | number | null): string {
    const key = this.unitPluralKey[unit ?? ''];

    return key ? this.i18n.translate(key, { count: Number(count) }) : '';
  }
}
